import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updateMetaTags, addJsonLd } from '../../utils/seo';

const AProposPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // SEO Meta Tags
    updateMetaTags({
      title: 'À propos de Vist Magazine - Notre mission et approche éditoriale',
      description: 'Vist Magazine est un média indépendant consacré à l\'analyse et au décryptage des tendances contemporaines. Découvrez notre mission, notre approche éditoriale et nos thématiques.',
      keywords: 'à propos, magazine indépendant, ligne éditoriale, mission, analyse tendances, média culturel',
      canonical: '/a-propos'
    });

    // Schema.org JSON-LD for About Page
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "À propos de Vist Magazine",
      "url": `${baseUrl}/a-propos`,
      "description": "Vist Magazine est un média indépendant consacré à l'analyse et au décryptage des tendances contemporaines",
      "publisher": {
        "@type": "Organization",
        "name": "Vist Magazine",
        "description": "Média indépendant d'analyse et de décryptage des tendances culturelles, sociétales, lifestyle et digitales",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png"
        },
        "foundingDate": "2025",
        "sameAs": [
          "https://facebook.com/vistmagazine",
          "https://twitter.com/vistmagazine",
          "https://instagram.com/vistmagazine"
        ]
      }
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const thematiques = [
    {
      icon: 'ri-book-open-line',
      title: 'Culture et société',
      description: 'Mouvements culturels, phénomènes sociaux et mutations contemporaines qui redéfinissent nos modes de vie et nos valeurs collectives.'
    },
    {
      icon: 'ri-heart-3-line',
      title: 'Lifestyle contemporain',
      description: 'Évolution des modes de vie, aspirations individuelles et nouvelles pratiques qui façonnent le quotidien des individus.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Médias et usages numériques',
      description: 'Transformation des médias, évolution des plateformes et impact des technologies sur la communication et l\'information.'
    },
    {
      icon: 'ri-global-line',
      title: 'Tendances du web et technologies',
      description: 'Innovations technologiques, nouveaux usages du web et leur influence sur la création de contenu et les pratiques culturelles.'
    }
  ];

  const valeurs = [
    {
      icon: 'ri-search-eye-line',
      title: 'Analyse approfondie',
      description: 'Nous privilégions la compréhension des phénomènes plutôt que leur simple description, en prenant le temps d\'examiner les tendances en profondeur.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Vérification rigoureuse',
      description: 'Chaque publication s\'appuie sur une recherche documentaire solide et une validation systématique des informations auprès de sources fiables.'
    },
    {
      icon: 'ri-scales-line',
      title: 'Neutralité éditoriale',
      description: 'Notre approche équilibrée privilégie l\'objectivité et la contextualisation, en croisant les perspectives pour offrir une vision nuancée.'
    },
    {
      icon: 'ri-time-line',
      title: 'Vision long terme',
      description: 'Nous nous intéressons aux mutations structurelles plutôt qu\'aux fluctuations éphémères, pour produire des contenus qui conservent leur pertinence.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="flex items-center justify-between h-[75px]">
            <Link to="/" className="flex items-center">
              <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-20" />
            </Link>
            
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Accueil</Link>
              <Link to="/magazine" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Magazine</Link>
              <Link to="/blog" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Blog</Link>
              <Link to="/a-propos" className="text-[15px] text-black font-medium transition-colors whitespace-nowrap">À propos</Link>
              <Link to="/contact" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Contact</Link>
            </div>

            <div className="flex items-center space-x-4">
              <button className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer">
                <i className="ri-search-line text-xl text-[#2a2a2a]"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors md:hidden cursor-pointer">
                <i className="ri-menu-line text-xl text-[#2a2a2a]"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-[180px] pb-[100px] px-8 bg-white">
        <div className="max-w-[900px] mx-auto">
          <h1 className="font-serif text-[56px] leading-[1.15] text-[#1a1a1a] mb-8 text-center">
            À propos de Vist Magazine
          </h1>
          <div className="space-y-6 text-[17px] text-[#555] leading-relaxed">
            <p>
              Vist Magazine est un média indépendant consacré à l'analyse et au décryptage des tendances contemporaines. Dans un environnement saturé d'informations instantanées et fragmentées, nous proposons une approche éditoriale fondée sur la profondeur, la contextualisation et le recul nécessaire à une compréhension claire des phénomènes qui façonnent notre époque.
            </p>
            <p>
              Notre publication s'adresse aux lecteurs qui recherchent une information structurée et documentée, au-delà du flux continu de l'actualité immédiate. Nous traitons des évolutions culturelles, sociétales, lifestyle et digitales avec une exigence de rigueur et de clarté, en privilégiant toujours l'analyse sur la réaction.
            </p>
          </div>
        </div>
      </section>

      {/* Notre Mission */}
      <section className="py-[100px] px-8 bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-[50%]">
              <img 
                src="https://public.readdy.ai/ai/img_res/854ff037156a202bddf9885ed590091e.jpg"
                alt="Notre mission"
                className="w-full h-[500px] object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
            <div className="lg:w-[50%]">
              <h2 className="font-serif text-[42px] text-[#1a1a1a] mb-8">Notre mission</h2>
              <div className="space-y-6 text-[16px] text-[#555] leading-relaxed">
                <p>
                  La vocation de Vist Magazine est d'apporter du recul et de la compréhension face aux mutations rapides qui caractérisent notre époque. Nous croyons que l'information de qualité ne se mesure pas à sa vitesse de diffusion, mais à sa capacité à éclairer les enjeux et à permettre une lecture structurée des évolutions en cours.
                </p>
                <p>
                  Notre mission consiste à produire des contenus qui aident nos lecteurs à développer une vision claire des phénomènes culturels, sociétaux et numériques. Nous nous attachons à contextualiser les tendances, à identifier les dynamiques durables et à distinguer l'essentiel de l'accessoire dans un environnement médiatique souvent dominé par l'instantanéité.
                </p>
                <p>
                  Cette approche éditoriale repose sur la conviction qu'une information bien documentée, analysée avec méthode et présentée avec clarté constitue un outil précieux pour comprendre le monde contemporain et ses transformations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approche Éditoriale */}
      <section className="py-[100px] px-8 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="font-serif text-[42px] text-center text-[#1a1a1a] mb-6">Une approche éditoriale indépendante</h2>
          <p className="text-[17px] text-[#666] text-center max-w-[800px] mx-auto mb-[60px] leading-relaxed">
            Notre ligne éditoriale repose sur des principes qui garantissent la qualité et la fiabilité de nos publications.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valeurs.map((valeur, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-black rounded-full mx-auto mb-6">
                  <i className={`${valeur.icon} text-[32px] text-white`}></i>
                </div>
                <h3 className="font-semibold text-[18px] text-[#1a1a1a] mb-3">
                  {valeur.title}
                </h3>
                <p className="text-[14px] text-[#666] leading-relaxed">
                  {valeur.description}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-[60px] max-w-[900px] mx-auto space-y-6 text-[16px] text-[#555] leading-relaxed">
            <p>
              L'importance de la neutralité guide l'ensemble de notre travail éditorial. Nous nous attachons à présenter les faits de manière équilibrée, en croisant les sources et en évitant les biais partisans. Cette exigence de rigueur s'accompagne d'un effort constant de vérification de l'information et de contextualisation des sujets traités.
            </p>
            <p>
              Le traitement approfondi des sujets constitue un autre pilier de notre approche. Plutôt que de multiplier les publications superficielles, nous privilégions des analyses détaillées qui permettent une compréhension réelle des phénomènes étudiés. Cette méthode demande du temps et des ressources, mais elle garantit la valeur et la durabilité de nos contenus.
            </p>
          </div>
        </div>
      </section>

      {/* Thématiques */}
      <section className="py-[100px] px-8 bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="font-serif text-[42px] text-center text-[#1a1a1a] mb-6">Les thématiques abordées</h2>
          <p className="text-[17px] text-[#666] text-center max-w-[800px] mx-auto mb-[60px] leading-relaxed">
            Vist Magazine couvre un ensemble de domaines qui reflètent les transformations contemporaines et leurs impacts sur nos modes de vie.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {thematiques.map((thematique, index) => (
              <div key={index} className="bg-white p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-black rounded-full mb-6">
                  <i className={`${thematique.icon} text-[28px] text-white`}></i>
                </div>
                <h3 className="font-semibold text-[20px] text-[#1a1a1a] mb-3 leading-[1.3]">
                  {thematique.title}
                </h3>
                <p className="text-[15px] text-[#666] leading-relaxed">
                  {thematique.description}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-[60px] max-w-[900px] mx-auto text-[16px] text-[#555] leading-relaxed">
            <p>
              Ces thématiques ne sont pas cloisonnées : elles s'entrecroisent et se nourrissent mutuellement pour offrir une vision globale des évolutions en cours. Notre objectif est de montrer comment les transformations culturelles, technologiques et sociales interagissent pour façonner le monde contemporain.
            </p>
          </div>
        </div>
      </section>

      {/* Public Cible */}
      <section className="py-[100px] px-8 bg-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-serif text-[42px] text-center text-[#1a1a1a] mb-8">À qui s'adresse Vist Magazine ?</h2>
          <div className="space-y-6 text-[16px] text-[#555] leading-relaxed">
            <p>
              Vist Magazine s'adresse à un lectorat diversifié, uni par une curiosité commune pour les évolutions contemporaines et un besoin de compréhension approfondie. Nos lecteurs recherchent une information claire, structurée et documentée, au-delà des flux d'actualité immédiate.
            </p>
            <p>
              Professionnels de la communication, créateurs de contenu, observateurs des mutations sociales, passionnés de culture digitale ou simplement lecteurs curieux : tous trouvent dans Vist Magazine des analyses qui les aident à mieux comprendre les phénomènes qui façonnent notre époque.
            </p>
            <p>
              Notre publication intéresse particulièrement ceux qui souhaitent développer une vision critique et informée des tendances, qu'il s'agisse d'évolutions technologiques, de mouvements culturels ou de transformations sociétales. Nous nous adressons à des lecteurs qui valorisent la profondeur sur la superficialité, et la réflexion sur la réaction.
            </p>
          </div>
        </div>
      </section>

      {/* Publication Responsable */}
      <section className="py-[100px] px-8 bg-[#1a1a1a] text-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-serif text-[42px] text-center mb-8">Une publication responsable</h2>
          <div className="space-y-6 text-[16px] text-[#ccc] leading-relaxed">
            <p>
              Vist Magazine s'engage à produire des contenus durables, pensés pour conserver leur pertinence au-delà de l'actualité immédiate. Cette approche implique un travail de documentation approfondi, une vérification systématique des informations et une mise à jour régulière de nos publications lorsque les évolutions le justifient.
            </p>
            <p>
              Nous accordons une attention particulière à l'accessibilité de nos contenus. Notre objectif est de rendre compréhensibles des sujets parfois complexes, sans pour autant sacrifier la rigueur de l'analyse. Cette exigence de clarté guide nos choix éditoriaux et notre manière de structurer l'information.
            </p>
            <p>
              L'engagement pour une publication responsable se traduit également par notre volonté de contribuer positivement à l'écosystème médiatique. Nous croyons qu'un journalisme de qualité, fondé sur la vérification des faits et la contextualisation, constitue un service essentiel dans un environnement où la désinformation et la superficialité menacent la qualité du débat public.
            </p>
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-[100px] px-8 bg-white">
        <div className="max-w-[900px] mx-auto text-center">
          <div className="bg-[#fafafa] p-12 rounded-2xl">
            <p className="font-serif text-[24px] text-[#1a1a1a] leading-relaxed italic mb-8">
              « Vist Magazine poursuit sa mission éditoriale avec la conviction qu'une information de qualité, analysée avec méthode et présentée avec clarté, demeure un outil précieux pour comprendre les mutations de notre époque. »
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/magazine" className="inline-flex items-center bg-black text-white px-8 py-3.5 rounded-full hover:bg-[#2a2a2a] transition-colors whitespace-nowrap cursor-pointer">
                Découvrir le magazine
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link to="/contact" className="inline-flex items-center border-2 border-black text-black px-8 py-3.5 rounded-full hover:bg-black hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white">
        <div className="max-w-[1280px] mx-auto px-8 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 pb-8 border-b border-white/10">
            <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-16 mb-4 md:mb-0" />
            <p className="font-serif italic text-[16px] text-[#aaa]">Analyses et regards éditoriaux sur le monde contemporain</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <h4 className="font-semibold text-[16px] mb-5 whitespace-nowrap">Navigation</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Accueil</Link></li>
                <li><Link to="/magazine" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Magazine</Link></li>
                <li><Link to="/blog" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Blog</Link></li>
                <li><Link to="/a-propos" className="text-[14px] text-white hover:text-[#ccc] transition-colors cursor-pointer whitespace-nowrap">À propos</Link></li>
                <li><Link to="/contact" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[16px] mb-5 whitespace-nowrap">Légal</h4>
              <ul className="space-y-3">
                <li><Link to="/mentions-legales" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Confidentialité</Link></li>
                <li><Link to="/conditions" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[16px] mb-5 whitespace-nowrap">Suivez-nous</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Facebook</a></li>
                <li><a href="#" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Twitter</a></li>
                <li><a href="#" className="text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Instagram</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[13px] text-[#999]">© {new Date().getFullYear()} Vist Magazine. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AProposPage;